import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { Pagination } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSale } from '../../../api/hooks/sales/useSale';
import { useSalesWithMark } from '../../../api/hooks/sales/useSalesWithMark';
import { InvoiceInList } from '../../../interfaces/invoice.interface';
import { ProductInOrder } from '../../../interfaces/product.interface';
import { Loading } from '../../atoms/Loading';
import { CorrelatedInvoicePickerCard } from './CorrelatedInvoicePickerCard';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  alreadyCorrelated: { id: string | null; mark: string }[];
  onSelectInvoice: (id: string, mark: string, products: ProductInOrder[]) => void;
  onManualAdd: (mark: string) => void;
  currentDocAadeCode?: string;
}

export const CorrelatedInvoicesModal = ({
  isOpen,
  onClose,
  customerId,
  alreadyCorrelated,
  onSelectInvoice,
  onManualAdd,
  currentDocAadeCode,
}: Props) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [manualMark, setManualMark] = useState('');
  const modal = useRef<HTMLIonModalElement>(null);

  const { data, isLoading } = useSalesWithMark(customerId, page, currentDocAadeCode);
  const { data: fullInvoice, isLoading: isLoadingFull } = useSale(selectedId ?? undefined);

  // Keep a stable ref to the latest onSelectInvoice callback so the effect below
  // does NOT need to list it as a dependency. If it were a dependency, any render
  // of the parent that produces a new callback reference (e.g. after setValues
  // changes correlatedInvoices) would re-trigger the effect while selectedId is
  // still the old value — causing an infinite loop specifically when the selected
  // invoice is already in the React Query cache (i.e. the cloned invoice itself).
  const onSelectInvoiceRef = useRef(onSelectInvoice);
  onSelectInvoiceRef.current = onSelectInvoice;

  useEffect(() => {
    if (fullInvoice && selectedId && fullInvoice.id === selectedId) {
      onSelectInvoiceRef.current(fullInvoice.id, fullInvoice.mark!, fullInvoice.products || []);
      setSelectedId(null);
    }
  }, [fullInvoice, selectedId]);

  const handleSelectFromList = useCallback((invoice: InvoiceInList) => {
    setSelectedId(invoice.id);
  }, []);

  const handleManualAdd = useCallback(() => {
    const trimmed = manualMark.trim();
    if (!trimmed) return;
    onManualAdd(trimmed);
    setManualMark('');
  }, [manualMark, onManualAdd]);

  const handleClose = useCallback(() => {
    setPage(1);
    setSelectedId(null);
    setManualMark('');
    onClose();
  }, [onClose]);

  return (
    <IonModal ref={modal} isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('SALE.SELECT_INVOICE_TO_CORRELATE')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>{t('SHARED.CANCEL')}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {(isLoading || isLoadingFull) && <Loading />}

        {/* Manual mark entry */}
        <div style={{ padding: '16px 16px 8px' }}>
          <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>{t('SALE.MANUAL_MARK_ENTRY')}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <IonItem className="flex-1 border-radius-input g-select-border" mode="md" style={{ flex: 1 }}>
              <IonInput
                mode="md"
                type="text"
                value={manualMark}
                data-testid="manual_mark_input"
                placeholder={t('SALE.CORRELATED_MARK_PLACEHOLDER')}
                onIonInput={(e) => setManualMark(((e.target as HTMLIonInputElement).value as string) || '')}
              />
            </IonItem>
            <IonButton
              size="small"
              data-testid="manual_mark_add_btn"
              onClick={handleManualAdd}
              disabled={!manualMark.trim()}>
              {t('SALE.ADD_CORRELATED')}
            </IonButton>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '8px 0' }} />

        {/* Invoice list */}
        <div style={{ padding: '8px 16px 12px', fontSize: 13, opacity: 0.7 }}>
          {t('SALE.SELECT_INVOICE_TO_CORRELATE')}
        </div>

        {!isLoading && data?.data?.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', opacity: 0.5 }}>{t('SALE.NO_MARKED_INVOICES')}</div>
        )}

        {data?.data?.map((invoice, i) => (
          <CorrelatedInvoicePickerCard
            key={invoice.id}
            index={i}
            data={invoice}
            onSelect={handleSelectFromList}
            isLoading={selectedId === invoice.id && isLoadingFull}
            alreadySelected={alreadyCorrelated.some((c) => c.mark === (invoice.mark ?? ''))}
          />
        ))}

        {(data?.total ?? 0) > 20 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <Pagination
              current={page}
              total={data?.total ?? 0}
              pageSize={20}
              onChange={setPage}
              size="small"
              hideOnSinglePage
            />
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};
